import { Season, SEASON_END_DAY, SEASON_MONTHS } from './seasons';

export enum SeasonalFavoriteKey {
  SpringFavorites = 'springFavorites',
  SummerFavorites = 'summerFavorites',
  FallFavorites = 'fallFavorites',
  WinterFavorites = 'winterFavorites',
}

export const SEASONAL_FAVORITE_KEY_TO_SEASON: Record<SeasonalFavoriteKey, Season> = {
  [SeasonalFavoriteKey.SpringFavorites]: 'SPRING',
  [SeasonalFavoriteKey.SummerFavorites]: 'SUMMER',
  [SeasonalFavoriteKey.FallFavorites]: 'FALL',
  [SeasonalFavoriteKey.WinterFavorites]: 'WINTER',
};

interface MonthDay {
  month: number;
  day: number;
}

export interface SeasonalFavoriteWindow {
  key: SeasonalFavoriteKey;
  start: MonthDay;
  end: MonthDay;
  enabled: boolean;
}

export const SEASONAL_FAVORITE_WINDOWS: SeasonalFavoriteWindow[] = Object.entries(SEASONAL_FAVORITE_KEY_TO_SEASON).map(
  ([key, season]) => ({
    key: key as SeasonalFavoriteKey,
    start: { month: SEASON_MONTHS[season].startMonth, day: 1 },
    end: { month: SEASON_MONTHS[season].endMonth, day: SEASON_END_DAY[season] },
    enabled: true,
  })
);

/**
 * Allows manually forcing a season without changing window definitions.
 * Set to null to rely on the automatic schedule above.
 */
export const SEASONAL_FAVORITE_OVERRIDE: SeasonalFavoriteKey | null = null;

export interface SeasonalFavoriteCopy {
  title: string;
  description: string;
  filterLabel: string;
}

export const SEASONAL_FAVORITE_COPY: Record<SeasonalFavoriteKey, SeasonalFavoriteCopy> = {
  [SeasonalFavoriteKey.SpringFavorites]: {
    title: 'Spring Flavors',
    description: 'Fresh seasonal dishes for spring',
    filterLabel: 'Spring flavors',
  },
  [SeasonalFavoriteKey.SummerFavorites]: {
    title: 'Summer Flavors',
    description: 'Light seasonal dishes for warm summer days',
    filterLabel: 'Summer flavors',
  },
  [SeasonalFavoriteKey.FallFavorites]: {
    title: 'Fall Flavors',
    description: 'Seasonal dishes perfect for autumn',
    filterLabel: 'Fall flavors',
  },
  [SeasonalFavoriteKey.WinterFavorites]: {
    title: 'Winter Flavors',
    description: 'Comforting seasonal dishes made for cozy winter meals',
    filterLabel: 'Winter flavors',
  },
};

interface SeasonalFavoriteOptions {
  referenceDate?: Date;
}

export function getActiveSeasonalFavoriteKey(options?: SeasonalFavoriteOptions): SeasonalFavoriteKey | null {
  if (SEASONAL_FAVORITE_OVERRIDE) {
    return SEASONAL_FAVORITE_OVERRIDE;
  }

  const referenceDate = options?.referenceDate ?? new Date();
  return (
    SEASONAL_FAVORITE_WINDOWS.find((window) => window.enabled && isWithinWindow(referenceDate, window))?.key ?? null
  );
}

export function isSeasonalFavoriteKeyActive(key: SeasonalFavoriteKey, options?: SeasonalFavoriteOptions): boolean {
  return getActiveSeasonalFavoriteKey(options) === key;
}

function isWithinWindow(date: Date, window: SeasonalFavoriteWindow): boolean {
  const current: MonthDay = {
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };

  const wrapsYear =
    window.start.month > window.end.month ||
    (window.start.month === window.end.month && window.start.day > window.end.day);

  if (!wrapsYear) {
    return isAfterOrSame(current, window.start) && isBeforeOrSame(current, window.end);
  }

  return isAfterOrSame(current, window.start) || isBeforeOrSame(current, window.end);
}

function isAfterOrSame(current: MonthDay, target: MonthDay): boolean {
  if (current.month > target.month) return true;
  if (current.month < target.month) return false;
  return current.day >= target.day;
}

function isBeforeOrSame(current: MonthDay, target: MonthDay): boolean {
  if (current.month < target.month) return true;
  if (current.month > target.month) return false;
  return current.day <= target.day;
}
